import './protokoll.css';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, errorText } from '../../api';
import { dayLabel, fmtDate, fmtTime } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { AREA_LABEL, type StaffArea } from '../../types';

// Zugriffsprotokoll (nur Admin): wer sich wann angemeldet, welchen Mandanten geöffnet und welche Datei angesehen
// oder heruntergeladen hat. Neueste zuerst, nach Tagen gruppiert, 50 je Seite. Filter stehen in der Adresse
// (?person=…&client=…&art=…), damit ein Link direkt gefiltert öffnet.

type Action = 'login' | 'login_failed' | 'login_locked' | 'login_code_failed' | 'client_open' | 'file_view' | 'file_download';

interface Item {
  id: number;
  at: string;
  action: Action;
  area: string | null;
  who: { id: string; name: string; kind: 'staff' | 'client'; isAdmin: boolean; area: StaffArea | null; firms: string[] } | null;
  client: { id: string; name: string } | null;
  file: string | null;
  fileGone: boolean;
  ip: string | null;
  device: string | null;
}

interface Page {
  keepMonths: number;
  more: boolean;
  items: Item[];
}

interface Filter {
  people: { id: string; name: string; isAdmin: boolean; area: StaffArea | null; active: boolean }[];
  clients: { id: string; name: string }[];
}

// „hat einen Beleg angesehen“
const AREA_NOUN: Record<string, string> = {
  beleg: 'einen Beleg',
  brief: 'einen Brief',
  anfrage: 'eine angeforderte Unterlage',
  chat: 'eine Datei aus dem Chat',
  bwa: 'eine BWA',
  ustva: 'ein UStVA-Protokoll',
  dokument: 'ein Dokument',
  rechnung: 'eine Rechnung',
  lohn: 'eine Lohnunterlage',
  export: 'den Export für die Betriebsprüfung',
  datev: 'den DATEV-Export der Belege',
  buchen: 'die Buchungsdatei für das Steuerprogramm',
};

export function Zugriffsprotokoll() {
  const [params, setParams] = useSearchParams();
  const person = params.get('person') ?? '';
  const client = params.get('client') ?? '';
  const art = params.get('art') ?? '';
  const query = new URLSearchParams(Object.entries({ person, client, art }).filter(([, v]) => v)).toString();
  const url = `/api/kanzlei/protokoll${query ? `?${query}` : ''}`;
  const res = useApi<Page>(url);
  const filter = useApi<Filter>('/api/kanzlei/protokoll/filter');
  // weitere Seiten („Mehr laden“), gehören zu genau dieser ersten Seite: Wird sie neu geladen (z. B. anderer Filter und
  // zurück), fängt „Mehr laden“ von vorn an, sonst fehlten Einträge von dazwischen
  const [extra, setExtra] = useState<{ first: Page; items: Item[]; more: boolean } | null>(null);
  const [busy, setBusy] = useState(false);
  const [moreError, setMoreError] = useState<{ first: Page; text: string } | null>(null);

  useEffect(() => {
    document.title = 'Zugriffsprotokoll · Elbcons Kanzlei';
  }, []);

  const set = (key: string, value: string) => {
    // vom aktuellen Stand der Adresse aus, nicht von „params“ dieser Anzeige: Werden zwei Filter schnell
    // hintereinander geändert (bevor neu angezeigt ist), ginge sonst der erste verloren
    const next = new URLSearchParams(window.location.search);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const d = res.data;
  const more = d && extra?.first === d ? extra : null;
  const items = [...(d?.items ?? []), ...(more?.items ?? [])];
  const hasMore = more ? more.more : !!d?.more;
  const moreFailed = d && moreError?.first === d ? moreError.text : null;

  async function loadMore() {
    const last = items.at(-1);
    if (!d || !last) return;
    setBusy(true);
    setMoreError(null);
    try {
      const sep = url.includes('?') ? '&' : '?';
      const p = await api.get<Page>(`${url}${sep}before=${last.id}`);
      setExtra({ first: d, items: [...(more?.items ?? []), ...p.items], more: p.more });
    } catch (err) {
      setMoreError({ first: d, text: errorText(err) });
    } finally {
      setBusy(false);
    }
  }

  const days: { label: string; items: Item[] }[] = [];
  for (const i of items) {
    const label = dayTitle(i.at);
    const last = days.at(-1);
    if (last?.label === label) last.items.push(i);
    else days.push({ label, items: [i] });
  }

  return (
    <div className="protokoll">
      <div className="head">
        <div>
          <h1>Zugriffsprotokoll</h1>
          <p>
            Wer sich wann angemeldet, welchen Mandanten geöffnet und welche Datei angesehen hat. Nur für den Admin sichtbar,
            Einträge werden nach {d?.keepMonths ?? 12} Monaten gelöscht.
          </p>
        </div>
      </div>

      <div className="card prot-filter">
        <div className="form">
          <div className="row">
            <label>
              Person
              <select value={person} onChange={(e) => set('person', e.target.value)}>
                <option value="">Alle</option>
                <option value="kanzlei">Alle aus der Kanzlei</option>
                <option value="mandanten">Alle Mandanten</option>
                {filter.data?.people.length ? (
                  <optgroup label="Kanzlei">
                    {filter.data.people.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                        {p.active ? '' : ' (deaktiviert)'}
                      </option>
                    ))}
                  </optgroup>
                ) : null}
              </select>
            </label>
            <label>
              Mandant
              <select value={client} onChange={(e) => set('client', e.target.value)}>
                <option value="">Alle</option>
                {filter.data?.clients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Was
              <select value={art} onChange={(e) => set('art', e.target.value)}>
                <option value="">Alles</option>
                <option value="anmeldung">Anmeldungen</option>
                <option value="mandant">Mandant geöffnet</option>
                <option value="datei">Dateien angesehen</option>
              </select>
            </label>
          </div>
        </div>
      </div>

      {!d ? (
        res.error ? (
          <p className="note">{res.error}</p>
        ) : (
          <div className="spinner" aria-label="Lädt" />
        )
      ) : items.length === 0 ? (
        <div className="card">
          <p className="note">{query ? 'Keine Einträge für diese Auswahl.' : 'Noch keine Einträge.'}</p>
        </div>
      ) : (
        <>
          {days.map((day) => (
            <section key={day.label} className="card prot-day" aria-label={day.label}>
              <h3>{day.label}</h3>
              <ul>
                {day.items.map((i) => (
                  <Row key={i.id} i={i} />
                ))}
              </ul>
            </section>
          ))}
          {hasMore && (
            <div className="prot-more">
              <button type="button" className="btn" disabled={busy} onClick={loadMore}>
                {busy ? 'Lädt …' : 'Mehr laden'}
              </button>
              {moreFailed && <p className="note err">{moreFailed}</p>}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// „Heute · 10.10.2026“, „Mittwoch · 08.10.2026“, ältere nur mit Datum
function dayTitle(at: string) {
  const label = dayLabel(at);
  const date = fmtDate(at);
  return /^\d/.test(label) ? date : `${label} · ${date}`;
}

function Row({ i }: { i: Item }) {
  const name = i.who?.name || 'Gelöschtes Konto';
  const bad = i.action === 'login_failed' || i.action === 'login_locked' || i.action === 'login_code_failed';
  return (
    <li className={`prot-row${bad ? ' bad' : ''}`}>
      <time dateTime={i.at}>{fmtTime(i.at)}</time>
      <div className="tx">
        <span>
          <b>{name}</b> {what(i)}
        </span>
        <small>{details(i)}</small>
      </div>
    </li>
  );
}

function what(i: Item) {
  switch (i.action) {
    case 'login':
      return 'hat sich angemeldet';
    case 'login_failed':
      return 'hat ein falsches Passwort eingegeben';
    case 'login_locked':
      return 'ist gesperrt: zu viele Fehlversuche bei der Anmeldung';
    case 'login_code_failed':
      return 'hat einen falschen Login-Code eingegeben';
    case 'client_open':
      return i.area === 'buchen' ? (
        <>
          hat die Buchungen von <b>{i.client?.name}</b> geöffnet
        </>
      ) : (
        <>
          hat <b>{i.client?.name}</b> geöffnet
        </>
      );
    case 'file_view':
    case 'file_download': {
      const noun = (i.area && AREA_NOUN[i.area]) ?? 'eine Datei';
      const verb = i.action === 'file_view' ? 'angesehen' : 'heruntergeladen';
      return (
        <>
          hat {noun} {verb}
          {i.file ? (
            <>
              : <span className="fn">{i.file}</span>
            </>
          ) : i.fileGone ? (
            ' (inzwischen gelöscht)'
          ) : null}
        </>
      );
    }
  }
}

// zweite Zeile: Rolle, bei Dateien der Mandant, bei Anmeldungen Gerät und IP-Adresse
function details(i: Item) {
  const parts: string[] = [];
  const w = i.who;
  if (w?.kind === 'client') {
    // Mandant: die Firma der Datei, bei Anmeldungen alle Firmen des Logins
    const firms = i.client ? [i.client.name] : w.firms;
    parts.push(firms.length ? `Mandant · ${firms.join(', ')}` : 'Mandant');
  } else {
    if (w) parts.push(w.isAdmin ? 'Admin' : AREA_LABEL[w.area ?? 'fibu']);
    if ((i.action === 'file_view' || i.action === 'file_download') && i.client) parts.push(i.client.name);
  }
  if (i.device) parts.push(i.device);
  if (i.ip) parts.push(`IP ${i.ip}`);
  return parts.join(' · ');
}
